import React, { useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { patch } from "@rails/request.js";
import { motion } from "framer-motion";

import RadioButtons from "./../shared/RadioButtons";
import { Toasts, useToasts } from "./../shared/Toast";
import EditableName from "./../shared/EditableName";
import ViewControls from "./../shared/ViewControls";
import OptionalColor from "./../shared/OptionalColor";
import ColorField from "./../shared/ColorField";
import Preview from "./Preview";
import EditKind from "./EditKind";
import ProofRow from "./../proof/ProofRow";
import HandPickedModal from "./HandPicked";

const SUGGESTED_PALETTES = {
  "Stagger":         { bg: "#F3F4F6", primary: "#8a74b9" },
  "Scrolling Wall":  { bg: "#0f172a", primary: "#3E8DFF" },
  "Masonry":         { bg: "#f8fafc", primary: "#3E8DFF" },
  "Carousel":        { bg: "#FFF8EE", primary: "#FF8F47" },
  "Slab":            { bg: "#0f172a", primary: "#FFD23A" },
  "Hero Quote":      { bg: "#FFF1F5", primary: "#FF7AAC" },
  "Hero Video":      { bg: "#0f172a", primary: "#3E8DFF" },
  "Tweet Grid":      { bg: "#F1F5F9", primary: "#1DA1F2" },
  "Audio Wall":      { bg: "#1e1b4b", primary: "#FF7AAC" },
  "Video Reels":     { bg: "#0f172a", primary: "#FF8F47" },
  "Avatar Strip":    { bg: "#FFFFFF", primary: "#3E8DFF" },
  "Stat Grid":       { bg: "#FFF8EE", primary: "#3E8DFF" },
  "Logo Bar":        { bg: "#FFFFFF", primary: "#0f172a" },
  "Floating Badge":  { bg: "#F8FAFC", primary: "#FFD23A" },
  "Floating Toast":  { bg: "#F8FAFC", primary: "#3E8DFF" },
  "Press Wall":      { bg: "#F8FAFC", primary: "#0f172a" },
  "Tabbed Wall":     { bg: "#f8fafc", primary: "#3E8DFF" },
  "Inline Quote":    { bg: "#FFFFFF", primary: "#3E8DFF" },
};

const SECTIONS = [
  { id: "kind-page",      label: "widget type",   color: "text-ssblue",   accent: "#3E8DFF" },
  { id: "selection-page", label: "select proofs", color: "text-ssgreen",  accent: "#3EB567" },
  { id: "design-page",    label: "design",        color: "text-ssorange", accent: "#FF8F47" },
];

export const WidgetEdit = ({ initWidget, projectId, backUrl, projectLabels = [], labelsUrl }) => {
  // Filter labels are tracked outside react-hook-form because the chips
  // are picked from a controlled list (project labels), and we send
  // ids rather than free-text — chip clicks don't need form coercion.
  const initialLabelIds = Array.isArray(initWidget.widget.label_ids) ? initWidget.widget.label_ids : [];
  const [filterLabelIds, setFilterLabelIds] = useState(initialLabelIds);
  // The widget JSON flattens selection, so the flag arrives at the top
  // level rather than nested under selection.
  const [requireAvatar, setRequireAvatar] = useState(!!initWidget.widget.require_avatar);
  const [widget, setWidget] = useState(initWidget.widget);
  const { toasts, setToasts, addToast } = useToasts();
  const [selectedProofs, setSelectedProofs] = useState([]);
  const [allProofs, setAllProofs] = useState([]);
  const [proofsLoading, setProofsLoading] = useState(true);
  const [page, setPage] = useState("kind-page");
  const [previewMode, setPreviewMode] = useState("desktop");
  const [breakoutOpen, setBreakoutOpen] = useState(false);

  const {
    formState,
    register,
    watch,
    reset,
    handleSubmit,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      kind: widget.kind,
      uuid: widget.uuid,
      automatic: widget.automatic,
      bg_color: widget.bg_color,
      primary_color: widget.primary_color,
      // Blank means "derive from the background", the behaviour before
      // these settings existed.
      show_dates: widget.show_dates !== false,
      text_color: widget.text_color || "",
      card_bg_color: widget.card_bg_color || "",
      highlight_color: widget.highlight_color || "",
      highlight_text_color: widget.highlight_text_color || "",
    },
  });

  const { isDirty, isLoading } = formState;

  const handleUpdate = (key, val) => {
    setValue(key, val, { shouldDirty: true, shouldValidate: true });
  };

  const suggestedColors = () => {
    const palette = SUGGESTED_PALETTES[widget.kind] || { bg: "#f8fafc", primary: "#3E8DFF" };
    handleUpdate("bg_color", palette.bg);
    handleUpdate("primary_color", palette.primary);
  };

  const fetchSelectedProofs = async () => {
    if (widget.automatic === "Automatic") return;
    const response = await fetch(`/api/v1/widgets/${initWidget.widget.uuid}/selected_proofs`);
    const data = await response.json();
    setSelectedProofs(data.proofs);
  };

  const fetchAllProofs = async () => {
    const response = await fetch(`/api/v1/widgets/${initWidget.widget.uuid}/available_proofs`);
    const data = await response.json();
    setAllProofs(data.proofs || []);
  };

  // Both lists start empty, so until these land the kind cards would claim
  // "no proofs yet" and the picker button would read "(0)". Hold that state
  // behind proofsLoading rather than showing a zero we don't believe.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([ fetchSelectedProofs(), fetchAllProofs() ]);
      } finally {
        if (!cancelled) setProofsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Autosave ────────────────────────────────────────────────────────
  // Same shape as the capform editor. Every change schedules a save,
  // debounced so a burst of keystrokes lands as one request.
  const saveTimer  = useRef(null);
  const savedTimer = useRef(null);
  const submitRef  = useRef(null);
  // onSubmit calls reset() with the server's response, which fires the very
  // watcher that schedules a save. Without this the editor saves in a loop.
  const applyingResponse = useRef(false);

  const notifySaved = () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      savedTimer.current = null;
      addToast("success", "Saved");
    }, 600);
  };

  const scheduleSave = () => {
    if (applyingResponse.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      submitRef.current?.();
    }, 800);
  };

  // Run a pending save now, so a change made a moment before navigating
  // away is not lost to the debounce window.
  const flushSave = async () => {
    if (!saveTimer.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    await submitRef.current?.();
  };

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  useEffect(() => {
    // Merge into prev — replacing with `value` (which only contains
    // react-hook-form registered fields) drops widget.name (owned by
    // the separate <EditableName>) and the header falls back to
    // "untitled" on every keystroke.
    const subscription = watch((value) => {
      setWidget((prev) => ({ ...prev, ...value }));
      scheduleSave();
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const inFlight = useRef(false);

  const onSubmit = async (data) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const response = await patch(`/api/v1/widgets/${initWidget.widget.id}`, {
      body: {
        widget: {
          kind: data.kind,
          selection: {
            automatic: data.automatic,
            require_avatar: requireAvatar,
            proof_ids: selectedProofs.map((proof) => proof.id),
          },
          // No-chip-selected = no filter (== all approved proofs).
          filter_label_ids: filterLabelIds,
          design: {
            bg_color: data.bg_color,
            primary_color: data.primary_color,
            show_dates: data.show_dates !== false,
            text_color: data.text_color || "",
            card_bg_color: data.card_bg_color || "",
            highlight_color: data.highlight_color || "",
            highlight_text_color: data.highlight_text_color || "",
          },
        },
      },
      contentType: "application/json",
      responseKind: "json",
    });
    inFlight.current = false;
    if (response.ok) {
      const json = await response.json;
      applyingResponse.current = true;
      reset(json.widget);
      setWidget(json.widget);
      if (Array.isArray(json.widget.label_ids)) setFilterLabelIds(json.widget.label_ids);
      setRequireAvatar(!!json.widget.require_avatar);
      // Let the watcher settle before listening again.
      setTimeout(() => { applyingResponse.current = false; }, 0);
      notifySaved();
    } else {
      addToast("error", "Error saving form");
    }
  };

  // handleSubmit is recreated each render; keep the ref pointing at a
  // current one so the debounce timer always fires the latest.
  submitRef.current = handleSubmit(onSubmit);

  const automatic = watch().automatic;
  const chosenBgColor = watch("bg_color");
  const chosenPrimaryColor = watch("primary_color");

  const toggleLabelId = (id) => {
    setFilterLabelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    scheduleSave();
  };

  const publicUrl = `/widgets/${initWidget.widget.uuid}`;

  const openBreakout = async (e) => {
    e?.preventDefault();
    // Autosave means there is nothing to prompt about; just make sure a
    // pending debounce lands before the preview reads saved state.
    await flushSave();
    setBreakoutOpen(true);
  };

  const closeBreakout = () => setBreakoutOpen(false);

  useEffect(() => {
    if (!breakoutOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeBreakout(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [breakoutOpen]);

  return (
    <div className="h-full flex flex-col">
      <Toasts toasts={toasts} setToasts={setToasts} />

      <header className="flex-none border-b-[3px] border-black bg-white">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2.5">
          <a
            href={backUrl || "/projects"}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-700 hover:bg-ssyellow hover:text-slate-900 shadow-[2px_2px_0_rgba(0,0,0,0.25)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.35)] hover:-translate-y-0.5 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            <span className="hidden sm:inline">back to widgets</span>
            <span className="sm:hidden">back</span>
          </a>

          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="hidden md:inline zz-hand text-lg text-ssgreen leading-none flex-none">editing —</span>
            <EditableName
              endpoint={`/api/v1/widgets/${initWidget.widget.id}`}
              resourceKey="widget"
              name={widget.name}
              fallback="untitled widget"
              onChange={(name) => setWidget((prev) => ({ ...prev, name }))}
              onError={(msg) => addToast("error", msg)}
              onSuccess={() => addToast("success", "Name updated")}
            />
          </div>

          <div className="flex-none flex items-center gap-1.5 sm:gap-2">
            <ViewControls
              mode={previewMode}
              setMode={setPreviewMode}
              onBreakout={openBreakout}
              breakoutActive={breakoutOpen}
            />
            {isLoading && (
              <svg className="animate-spin h-4 w-4 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 p-3 sm:p-5">
          <aside className="w-full md:w-1/2 md:flex-none">
            <div className="bg-white border-[3px] border-black md:h-[calc(100vh-160px)] md:overflow-y-auto">
              <div className="divide-y-[3px] divide-black">
                {SECTIONS.map((s) => {
                  const isOpen = page === s.id;
                  return (
                    <div key={s.id}>
                      <button
                        type="button"
                        onClick={() => setPage(isOpen ? null : s.id)}
                        aria-expanded={isOpen}
                        className={`w-full px-5 py-4 flex justify-between items-center text-left transition-colors ${isOpen ? "bg-slate-50" : "hover:bg-slate-50"}`}
                      >
                        <span className={`zz-hand text-2xl ${s.color}`}>{s.label}</span>
                        <span
                          className={`text-xl font-black transition-transform ${isOpen ? "rotate-90" : ""}`}
                          style={{ color: s.accent }}
                          aria-hidden="true"
                        >
                          ›
                        </span>
                      </button>
                      <motion.div
                        initial={false}
                        animate={{
                          height: isOpen ? "auto" : 0,
                          display: isOpen ? "block" : "none",
                          opacity: isOpen ? 1 : 0,
                        }}
                        className="relative z-20 overflow-hidden"
                      >
                        {s.id === "kind-page" && (
                          <div className="p-4">
                            <EditKind kind={widget.kind} onUpdate={handleUpdate} proofs={allProofs} proofsLoading={proofsLoading} />
                          </div>
                        )}
                        {s.id === "selection-page" && (
                          <div className="flex flex-col gap-y-3 p-4">
                            <RadioButtons
                              kind="selection"
                              buttons={["Automatic", "Hand Picked"]}
                              selected={automatic}
                              setSelected={(val) => handleUpdate("automatic", val)}
                            />
                            {automatic === "Hand Picked" && (
                              <div className="flex flex-col gap-y-2 mt-2">
                                <HandPickedModal widget={widget} setSelectedProofs={setSelectedProofs} selectedProofs={selectedProofs} proofsLoading={proofsLoading} />
                                {selectedProofs.length > 0 && (
                                  <div className="flex flex-col mt-2 space-y-3">
                                    {selectedProofs.map((proof, index) => (
                                      <ProofRow key={proof.uuid} initProof={proof} addToast={addToast} index={index} />
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {automatic === "Automatic" && (
                              <div className="flex flex-col gap-y-3 mt-2">
                                <div className="zz-panel text-sm text-slate-700">
                                  <p className="zz-hand text-xl text-ssgreen leading-none mb-1">on autopilot —</p>
                                  <p>This widget will be automatically populated when you <b>approve</b> a proof.</p>
                                </div>
                                <div className="zz-panel">
                                  <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={requireAvatar}
                                      onChange={(e) => { setRequireAvatar(e.target.checked); scheduleSave(); }}
                                      className="mt-0.5 h-4 w-4 accent-ssgreen"
                                    />
                                    <span>
                                      <span className="font-bold text-sm">Require an avatar</span>
                                      <span className="block text-[11px] text-slate-500">
                                        Only show proofs whose contact has a picture, so the
                                        widget does not fill with blank circles.
                                      </span>
                                    </span>
                                  </label>
                                </div>
                                <div className="zz-panel">
                                  <div className="flex flex-col gap-y-3">
                                    <p className="zz-hand text-xl text-sspink leading-none">filter by label —</p>
                                    {projectLabels.length === 0 ? (
                                      <p className="text-xs text-slate-500 italic">
                                        No labels in this project yet. Add some on the <a href={labelsUrl} className="zz-link">labels page</a> first.
                                      </p>
                                    ) : (
                                      <div className="flex flex-wrap gap-1.5">
                                        {(() => {
                                          const allOn = filterLabelIds.length === 0;
                                          return (
                                            <button
                                              type="button"
                                              onClick={() => { setFilterLabelIds([]); scheduleSave(); }}
                                              className={
                                                "px-2.5 py-1 text-xs font-bold rounded-full border-[2px] border-black transition-colors " +
                                                (allOn ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-100")
                                              }
                                            >
                                              all approved proofs
                                            </button>
                                          );
                                        })()}
                                        {projectLabels.map((label) => {
                                          const on = filterLabelIds.includes(label.id);
                                          const count = label.approved_proof_count ?? 0;
                                          const dim = count === 0 && !on;
                                          return (
                                            <button
                                              key={label.id}
                                              type="button"
                                              onClick={() => toggleLabelId(label.id)}
                                              title={count === 0 ? "No approved proofs carry this label yet" : `${count} approved proof${count === 1 ? "" : "s"}`}
                                              className={
                                                "px-2.5 py-1 text-xs font-bold rounded-full border-[2px] border-black transition-colors inline-flex items-center gap-1 " +
                                                (on ? "text-white" : "bg-white text-slate-700 hover:bg-slate-100") +
                                                (dim ? " opacity-50" : "")
                                              }
                                              style={on ? { backgroundColor: label.color } : {}}
                                            >
                                              <span>{label.name}</span>
                                              <span className={"text-[10px] " + (on ? "text-white/80" : "text-slate-400")}>({count})</span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {s.id === "design-page" && (
                          <div className="flex flex-col gap-y-4 p-4">
                            <div>
                              <button type="button" onClick={suggestedColors} className="zz-btn sm blue">
                                use suggested colors
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="zz-label">Background</span>
                                <ColorField value={chosenBgColor}
                                            onChange={(v) => handleUpdate("bg_color", v)} />
                              </div>
                              <div>
                                <span className="zz-label">Primary</span>
                                <ColorField value={chosenPrimaryColor}
                                            onChange={(v) => handleUpdate("primary_color", v)} />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                              <OptionalColor label="Text" value={watch("text_color")}
                                             onChange={(v) => handleUpdate("text_color", v)} />
                              <OptionalColor label="Card background" value={watch("card_bg_color")}
                                             onChange={(v) => handleUpdate("card_bg_color", v)} />
                              <OptionalColor label="Highlight" value={watch("highlight_color")}
                                             onChange={(v) => handleUpdate("highlight_color", v)} />
                              <OptionalColor label="Highlight text" value={watch("highlight_text_color")}
                                             onChange={(v) => handleUpdate("highlight_text_color", v)} />
                            </div>
                            <label className="flex items-start gap-2 cursor-pointer mt-4">
                              <input
                                type="checkbox"
                                checked={watch("show_dates") !== false}
                                onChange={(e) => handleUpdate("show_dates", e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-ssblue"
                              />
                              <span>
                                <span className="font-bold text-sm">Show dates</span>
                                <span className="block text-[11px] text-slate-500">
                                  Dates are not compatible with all widgets.
                                </span>
                              </span>
                            </label>

                          </div>
                        )}
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="w-full md:flex-1 min-w-0">
            <Preview
              widget={widget}
              fetchMethod={automatic}
              selectedProofs={selectedProofs}
              filterLabelIds={filterLabelIds}
              requireAvatar={requireAvatar}
              previewMode={previewMode}
            />
          </section>
        </div>
      </div>

      {breakoutOpen && (
        <div className="fixed inset-0 z-[100] bg-black">
          <iframe
            src={publicUrl}
            className="w-full h-full block bg-white border-0"
            title="Widget fullscreen preview"
          />
          <button
            type="button"
            onClick={closeBreakout}
            className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-900 hover:bg-ssyellow shadow-[3px_3px_0_rgba(0,0,0,0.4)] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
            exit fullscreen
            <span className="hidden sm:inline opacity-50 ml-1">esc</span>
          </button>
        </div>
      )}
    </div>
  );
};

